<script setup lang="ts" generic="TSchema extends Object, TStopSchemaKeys extends FormAllAvailableKeys<TSchema>">
import type { BlockUIPassThroughOptions } from 'primevue/blockui';
import type { Component } from 'vue';
import { useTranslation } from 'i18next-vue';
import { get } from 'lodash-es';
import {
  computed,
  onMounted,
  ref,
} from 'vue';

import VButton from '@components/buttons/VButton/VButton.vue';
import VEmpty from '@components/empty/VEmpty/VEmpty';
import VLoader from '@components/loader/VLoader.vue';
import VFormCheckbox from '@modules/forms/components/inputs/VFormCheckbox/VFormCheckbox.vue';
import VFormColorInput from '@modules/forms/components/inputs/VFormColorInput/VFormColorInput.vue';
import VFormDateInput from '@modules/forms/components/inputs/VFormDateInput/VFormDateInput.vue';
import VFormDateRangeInput from '@modules/forms/components/inputs/VFormDateRangeInput/VFormDateRangeInput.vue';
import VFormFilesInput from '@modules/forms/components/inputs/VFormFilesInput/VFormFilesInput.vue';
import VFormHiddenInput from '@modules/forms/components/inputs/VFormHiddenInput/VFormHiddenInput.vue';
import VFormMultiselectBox from '@modules/forms/components/inputs/VFormMultiselectBox/VFormMultiselectBox.vue';
import VFormNumberInput from '@modules/forms/components/inputs/VFormNumberInput/VFormNumberInput.vue';
import VFormPasswordInput from '@modules/forms/components/inputs/VFormPasswordInput/VFormPasswordInput.vue';
import VFormRadioInput from '@modules/forms/components/inputs/VFormRadioInput/VFormRadioInput.vue';
import VFormSelectButtonInput from '@modules/forms/components/inputs/VFormSelectButtonInput/VFormSelectButtonInput.vue';
import VFormSelectInput from '@modules/forms/components/inputs/VFormSelectInput/VFormSelectInput.vue';
import VFormSingleFileInput from '@modules/forms/components/inputs/VFormSingleFileInput/VFormSingleFileInput.vue';
import VFormSwitchInput from '@modules/forms/components/inputs/VFormSwitchInput/VFormSwitchInput.vue';
import VFormTextarea from '@modules/forms/components/inputs/VFormTextarea/VFormTextarea.vue';
import VFormTextEdit from '@modules/forms/components/inputs/VFormTextEdit/VFormTextEdit.vue';
import VFormTextInput from '@modules/forms/components/inputs/VFormTextInput/VFormTextInput.vue';
import { TRANSLATION_GROUP_FORMS_COMMON } from '@modules/forms/constants';
import { EFormInputTypes } from '@modules/forms/enums';
import {
  flattenSchema,
  isKeyOfSchema,
  isSchemaInput,
} from '@modules/forms/utils';
import { useI18n } from '@modules/i18n/composables';
import {
  assert,
  isNumber,
} from '@utils/guards';
import {
  watchDeep,
  watchImmediate,
} from '@vueuse/core';

import type {
  FlattenFormSchema,
  FlattenSchema,
  FormAllAvailableKeys,
  FormComponentDataMap,
  FormFlattenedErrors,
  FormFlattenKey,
  FormInnerGroup,
  FormResult,
  FormSchema,
  FormSchemaErrors,
  VFormEmits,
  VFormProps,
  VFormRef,
  VFormSlots,
} from './types';

const props = withDefaults(defineProps<VFormProps<TSchema, TStopSchemaKeys>>(), {
  customErrors: () => ({}),
  groups: () => ([]),
});
const emit = defineEmits<VFormEmits<TSchema, TStopSchemaKeys>>();

defineSlots<VFormSlots<TSchema, TStopSchemaKeys>>();

const { getTranslation } = useI18n();
const { t } = useTranslation();

const ptBlockUI: BlockUIPassThroughOptions = {
  mask: { style: 'cursor: wait', class: '!bg-white/50' },
};

function flattenErrors(
  schema: FlattenFormSchema<FlattenSchema<TSchema, TStopSchemaKeys>>,
  errors: FormSchemaErrors<TSchema, TStopSchemaKeys>,
): FormFlattenedErrors<TSchema, TStopSchemaKeys> {
  return Object.keys(schema).reduce((accumulator, key) => {
    Object.assign(accumulator, {
      [key]: get(errors, key),
    });
    return accumulator;
  }, {} as FormFlattenedErrors<TSchema, TStopSchemaKeys>);
}

const flattenedSchema = computed<FlattenFormSchema<FlattenSchema<TSchema, TStopSchemaKeys>>>(() => flattenSchema(props.schema));
const formErrors = ref<Partial<Record<FormFlattenKey<TSchema, TStopSchemaKeys>, string[]>>>();
const formTouched = ref<Partial<Record<FormFlattenKey<TSchema, TStopSchemaKeys>, boolean>>>();
const formValues = ref<Partial<FlattenSchema<TSchema, TStopSchemaKeys>>>();
const flattenedCustomErrors = computed(() => flattenErrors(flattenedSchema.value, props.customErrors));
const flattenedSchemaKeys = computed(() => Object.keys(flattenedSchema.value));

const initialValues = computed<Partial<FlattenSchema<TSchema, TStopSchemaKeys>>>(() => (
  Object.entries(flattenedSchema.value).reduce((accumulator, [key, item]) => {
    assert(isKeyOfSchema(flattenedSchema.value, key));
    assert(isSchemaInput(item));

    Object.assign(accumulator, {
      [key]: item.value,
    });

    return accumulator;
  }, {} as Partial<FlattenSchema<TSchema, TStopSchemaKeys>>)
));

function syncValues(newValue: Partial<FlattenSchema<TSchema, TStopSchemaKeys>>, oldValue?: Partial<FlattenSchema<TSchema, TStopSchemaKeys>>): void {
  const valuesDiff: Partial<FlattenSchema<TSchema, TStopSchemaKeys>> = {};
  for (const [key, value] of Object.entries(newValue)) {
    assert(isKeyOfSchema(flattenedSchema.value, key));

    if (oldValue?.[key] !== value) {
      Object.assign(valuesDiff, {
        [key]: value,
      });
    }
  }

  if (!formValues.value) {
    formValues.value = {};
  }

  formValues.value = {
    ...formValues.value,
    ...valuesDiff,
  };

  if (formTouched.value) {
    for (const key of Object.keys(valuesDiff)) {
      formTouched.value = {
        ...formTouched.value,
        [key]: false,
      };
    }
  }

  syncErrors();
}

watchImmediate(initialValues, (newValue, oldValue) => {
  syncValues(newValue, oldValue);
}, { deep: true });

function getValues(schema: FormSchema<TSchema, TStopSchemaKeys>, prefix = ''): TSchema {
  return Object.entries(schema).reduce((accumulator, [key, value]) => {
    if (isSchemaInput(value)) {
      const formKey = `${prefix}${key}`;
      assert(isKeyOfSchema(flattenedSchema.value, formKey));

      Object.assign(accumulator, {
        [key]: formValues.value?.[formKey],
      });
    } else {
      Object.assign(accumulator, {
        [key]: getValues(value, `${prefix}${key}.`),
      });
    }

    return accumulator;
  }, {} as TSchema);
}

const formResult = computed<FormResult<TSchema>>(() => ({
  values: getValues(props.schema),
}));

function getErrors(key: FormFlattenKey<TSchema, TStopSchemaKeys>): string[] {
  return formErrors.value?.[key] ?? [];
}

function getComponent(type: EFormInputTypes): Component {
  switch (type) {
    case EFormInputTypes.TEXT: {
      return VFormTextInput;
    }
    case EFormInputTypes.NUMBER: {
      return VFormNumberInput;
    }
    case EFormInputTypes.PASSWORD: {
      return VFormPasswordInput;
    }
    case EFormInputTypes.CHECKBOX: {
      return VFormCheckbox;
    }
    case EFormInputTypes.SELECT: {
      return VFormSelectInput;
    }
    case EFormInputTypes.DATE: {
      return VFormDateInput;
    }
    case EFormInputTypes.DATE_RANGE: {
      return VFormDateRangeInput;
    }
    case EFormInputTypes.COLOR: {
      return VFormColorInput;
    }
    case EFormInputTypes.MULTISELECT_BOX: {
      return VFormMultiselectBox;
    }
    case EFormInputTypes.TEXTAREA: {
      return VFormTextarea;
    }
    case EFormInputTypes.TEXT_EDITOR: {
      return VFormTextEdit;
    }
    case EFormInputTypes.SWITCH: {
      return VFormSwitchInput;
    }
    case EFormInputTypes.RADIO: {
      return VFormRadioInput;
    }
    case EFormInputTypes.SELECT_BUTTON: {
      return VFormSelectButtonInput;
    }
    case EFormInputTypes.FILES: {
      return VFormFilesInput;
    }
    case EFormInputTypes.SINGLE_FILE: {
      return VFormSingleFileInput;
    }
    case EFormInputTypes.HIDDEN: {
      return VFormHiddenInput;
    }
    case EFormInputTypes.SLOT: {
      return VEmpty;
    }
    default:
      throw new Error('Unknown input type');
  }
}

const componentsData = computed<FormComponentDataMap<TSchema, TStopSchemaKeys>>(() => (
  Object.entries(flattenedSchema.value).reduce((accumulator, [key, item]) => {
    assert(isKeyOfSchema(flattenedSchema.value, key));
    assert(isSchemaInput(item));
    const { type, ...restPropsData } = item;
    const errors = getErrors(key);

    Object.assign(accumulator, {
      [key]: {
        component: getComponent(type),
        key,
        props: {
          ...restPropsData,
          value: item.value ?? formValues.value?.[key],
          name: key,
          isInvalid: errors.length > 0,
          errors,
          readonly: props.readonly || item.readonly,
        },
      },
    });

    return accumulator;
  }, {} as FormComponentDataMap<TSchema, TStopSchemaKeys>)
));

const formGroups = computed<FormInnerGroup<
  TSchema,
  TStopSchemaKeys
>[]>(() => {
  if (props.groups.length > 0) {
    return props.groups.map((item) => ({
      title: item.title,
      isLoading: item.isLoading,
      items: item.items.filter((itemKey) => isKeyOfSchema(flattenedSchema.value, itemKey)).map((itemKey) => {
        assert(isKeyOfSchema(flattenedSchema.value, itemKey));
        return componentsData.value[itemKey];
      }),
      minHeight: item.minHeight,
    }));
  }

  return [{
    items: Object.values(componentsData.value),
  }];
});

function isEmptyValue(key: FormFlattenKey<TSchema, TStopSchemaKeys>): boolean {
  const value = formValues.value?.[key];

  return Array.isArray(value)
    ? !value.length
    : (
        value == null || value === ''
      );
}

function validateField(key: FormFlattenKey<TSchema, TStopSchemaKeys>): string[] {
  if (!formTouched.value?.[key]) {
    return [];
  }

  const schemaValue = flattenedSchema.value[key];
  const validation = schemaValue?.validation ?? {};
  const value = formValues.value?.[key];

  const errors: string[] = [];
  if (!!schemaValue?.required && isEmptyValue(key)) {
    errors.push(schemaValue.requiredErrorText || getTranslation(TRANSLATION_GROUP_FORMS_COMMON, 'ERROR_REQUIRED').value);

    // Do not show other errors if empty
    return errors;
  }

  if (validation.minValue && (!isNumber(value) || value < validation.minValue.value)) {
    errors.push(validation.minValue.message || getTranslation(TRANSLATION_GROUP_FORMS_COMMON, 'ERROR_INVALID').value);
  }

  if (validation.maxValue && (!isNumber(value) || value > validation.maxValue.value)) {
    errors.push(validation.maxValue.message || getTranslation(TRANSLATION_GROUP_FORMS_COMMON, 'ERROR_INVALID').value);
  }

  return errors;
}

function getFieldErrors(key: FormFlattenKey<TSchema, TStopSchemaKeys>): string[] | undefined {
  const errors = validateField(key);

  const customErrors = flattenedCustomErrors.value[key];
  if (customErrors) {
    errors.push(...(customErrors.map((item) => t(item.locale_key, item.values))));
  }

  return errors.length ? errors : undefined;
}

function setValue(
  key: FormFlattenKey<TSchema, TStopSchemaKeys>,
  value?: FlattenSchema<TSchema, TStopSchemaKeys>[FormFlattenKey<TSchema, TStopSchemaKeys>],
): void {
  if (!formValues.value) {
    formValues.value = {};
  }

  if (!formErrors.value) {
    formErrors.value = {};
  }

  formValues.value[key] = value;
  formErrors.value[key] = getFieldErrors(key);

  if (props.submitOnChange) {
    submit();
  }
}

function emitInteracted(
  key: FormFlattenKey<TSchema, TStopSchemaKeys>,
  value?: FlattenSchema<TSchema, TStopSchemaKeys>[FormFlattenKey<TSchema, TStopSchemaKeys>],
): void {
  emit('interacted', key, value, formResult.value);
}

function onBlur(
  key: FormFlattenKey<TSchema, TStopSchemaKeys>,
  value?: FlattenSchema<TSchema, TStopSchemaKeys>[FormFlattenKey<TSchema, TStopSchemaKeys>],
): void {
  if (!formTouched.value) {
    formTouched.value = {};
  }

  formTouched.value[key] = true;
  setValue(key, value);
  if (isKeyOfSchema(flattenedSchema.value, key)) {
    emit('blur', key, value, formResult.value);
  }
}

function onFocus(
  key: FormFlattenKey<TSchema, TStopSchemaKeys>,
  value?: FlattenSchema<TSchema, TStopSchemaKeys>[FormFlattenKey<TSchema, TStopSchemaKeys>],
): void {
  setValue(key, value);
  emit('focus', key, value, formResult.value);
}

function onChange(
  key: FormFlattenKey<TSchema, TStopSchemaKeys>,
  value?: FlattenSchema<TSchema, TStopSchemaKeys>[FormFlattenKey<TSchema, TStopSchemaKeys>],
): void {
  setValue(key, value);
  emit('change', key, value, formResult.value);
  emitInteracted(key, value);
}

function onInput(
  key: FormFlattenKey<TSchema, TStopSchemaKeys>,
  value?: FlattenSchema<TSchema, TStopSchemaKeys>[FormFlattenKey<TSchema, TStopSchemaKeys>],
): void {
  setValue(key, value);
  emit('input', key, value, formResult.value);
  emitInteracted(key, value);
}

function syncErrors(): void {
  formErrors.value = {};

  for (const key of Object.keys(flattenedSchema.value)) {
    assert(isKeyOfSchema(flattenedSchema.value, key));
    formErrors.value[key] = getFieldErrors(key);
  }
}

function syncTouched(): void {
  if (!formTouched.value) {
    formTouched.value = {};
  }

  for (const key of Object.keys(flattenedSchema.value)) {
    assert(isKeyOfSchema(flattenedSchema.value, key));
    formTouched.value[key] = formTouched.value[key] ?? false;
  }
}

function hasErrors(): boolean {
  return !!formErrors.value && Object.values(formErrors.value).some((item) => !!item);
}

function setAllTouched(): void {
  for (const key of Object.keys(flattenedSchema.value)) {
    assert(isKeyOfSchema(flattenedSchema.value, key));
    if (!formTouched.value) {
      formTouched.value = {};
    }

    formTouched.value[key] = true;
  }
}

function reset(): void {
  formErrors.value = undefined;
  formTouched.value = undefined;
  formValues.value = undefined;
  syncValues(initialValues.value);
}

function submit(): void {
  if (props.disabled) {
    return;
  }

  setAllTouched();
  syncErrors();

  if (!hasErrors()) {
    emit('submit', formResult.value.values);
  }
}

onMounted(() => {
  if (Object.keys(props.customErrors).length > 0) {
    syncErrors();
  }
});

watchDeep(() => props.customErrors, syncErrors);

watchDeep(flattenedSchemaKeys, () => {
  syncErrors();
  syncTouched();
});

defineExpose<VFormRef>({
  submit,
  reset,
});

const baseItemClassName = 'mb-4 last:mb-0';
</script>

<template>
  <form v-data-qa-auto-id="'VForm'"
    novalidate
    @submit.prevent="submit"
  >
    <VLoader
      v-for="(group, index) in formGroups"
      :key="index"
      :loading="group.isLoading"
      :min-height="group.minHeight"
    >
      <PBlockUI
        v-data-test="{ el: 'form-group', index }"
        :blocked="isLoading"
        :base-z-index="10"
        :auto-z-index="false"
        :pt="ptBlockUI"
        class="mt-4 first:mt-0"
      >
        <div>
          <div
            v-if="group.title"
            v-data-test="{ el: 'form-group-title' }"
            class="mb-3 text-lg font-medium"
            :class="{ 'text-center': isGroupTitleCentered }"
          >
            {{ group.title }}
          </div>
          <template
            v-for="data in group.items"
            :key="data.key"
          >
            <slot
              :name="data.key"
              :readonly="data.props.readonly"
              :errors="data.props.errors"
              :on-focus="onFocus"
              :class-name="baseItemClassName"
              :on-blur="onBlur"
              :on-change="onChange"
              :on-input="onInput"
            >
              <component
                :is="data.component"
                v-bind="data.props"
                v-show="!data.props.isHidden"
                :class="baseItemClassName"
                @blur="onBlur(data.key, $event)"
                @focus="onFocus(data.key, $event)"
                @change="onChange(data.key, $event)"
                @input="onInput(data.key, $event)"
                @upload="submit"
              />
            </slot>
          </template>
        </div>
      </PBlockUI>
    </VLoader>
    <slot name="prefooter" />
    <div
      v-if="submitBtnLabel || $slots.secondaryBtn"
      class="mt-4 flex items-center gap-2"
      :class="{
        'justify-center': isButtonsCentered,
        'flex-row-reverse': reverseButtons,
      }"
    >
      <VButton
        v-if="submitBtnLabel && !readonly"
        v-data-test="{ el: 'form-submit' }"
        :label="submitBtnLabel"
        :icon="submitBtnIcon"
        :severity="submitBtnSeverity"
        :size="submitBtnSize"
        type="submit"
        :loading="isLoading"
        :disabled="disabled"
      />
      <slot
        name="secondaryBtn"
      />
    </div>
  </form>
</template>
